
import { BarChart3, AlertCircle, CheckCircle, FileSearch, Brain } from 'lucide-react';
import { PhaseLayout } from "./PhaseLayout";

export const PhaseTwo = () => {
  return (
    <PhaseLayout 
      order={2}
      icon={<BarChart3 className="text-white w-8 h-8" />}
      title="Phase 2: Risk Assessment & Analytics"
      description="Our AI-powered analytics engine processes collected data to assess claim validity, detect risks, and enhance underwriting accuracy."
      steps={[
        {
          number: "1",
          title: "Advanced Analytics Engine",
          description: "Applies proprietary AI models to evaluate claim viability"
        },
        {
          number: "2",
          title: "Initial Underwriting Score",
          description: "Generates a risk score based on available information"
        },
        {
          number: "3",
          title: "Automated Data Requests",
          description: "Identifies missing information and requests it automatically"
        }
      ]}
      contentLeft={<RiskAnalysisIllustration />}
      reversed={true}
    />
  );
};

const RiskAnalysisIllustration = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 transform transition-transform hover:scale-105">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-medium text-gray-800">AI Risk Analysis</h4>
            <p className="text-sm text-gray-500">John Property Claim #JPC-2023</p>
          </div>
          
          <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center">
            <span className="text-blue-700 font-bold text-2xl">76</span>
          </div>
        </div>
        
        <div className="mt-6 space-y-3">
          {/* Key Analytics Metrics with Text */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-700">Evidence Strength</span>
              <span className="text-sm font-medium text-gray-700">82%</span>
            </div>
            <div className="h-2 relative rounded-full overflow-hidden bg-gray-200">
              <div className="h-full bg-blue-500 rounded-full absolute top-0 left-0" style={{ width: '82%' }}></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-700">Claim Accuracy</span>
              <span className="text-sm font-medium text-gray-700">76%</span>
            </div>
            <div className="h-2 relative rounded-full overflow-hidden bg-gray-200">
              <div className="h-full bg-teal-500 rounded-full absolute top-0 left-0" style={{ width: '76%' }}></div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-700">Documentation Quality</span>
              <span className="text-sm font-medium text-gray-700">68%</span>
            </div>
            <div className="h-2 relative rounded-full overflow-hidden bg-gray-200">
              <div className="h-full bg-purple-500 rounded-full absolute top-0 left-0" style={{ width: '68%' }}></div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 mb-3">Risk Assessment Categories</h5>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center rounded-lg bg-yellow-50 p-2">
            <div className="bg-yellow-100 rounded-full p-2 w-10 h-10 mx-auto mb-1 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-yellow-700" />
            </div>
            <p className="text-xs font-medium text-yellow-800">Medium Risk</p>
            <p className="text-xs text-yellow-600">Weather</p>
          </div>
          
          <div className="text-center rounded-lg bg-green-50 p-2">
            <div className="bg-green-100 rounded-full p-2 w-10 h-10 mx-auto mb-1 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-700" />
            </div>
            <p className="text-xs font-medium text-green-800">Low Risk</p>
            <p className="text-xs text-green-600">Policy</p>
          </div>
          
          <div className="text-center rounded-lg bg-blue-50 p-2">
            <div className="bg-blue-100 rounded-full p-2 w-10 h-10 mx-auto mb-1 flex items-center justify-center">
              <FileSearch className="w-6 h-6 text-blue-700" />
            </div>
            <p className="text-xs font-medium text-blue-800">Need Data</p>
            <p className="text-xs text-blue-600">Damages</p>
          </div>
        </div>
      </div>
      
      <div className="mt-4 bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-sm font-medium text-gray-700">AI Processing Status</h5>
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">In Progress</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="bg-wamy-100 rounded-full p-1 w-8 h-8 flex items-center justify-center">
            <Brain className="w-4 h-4 text-wamy-600" />
          </div>
          <div className="text-xs text-gray-600">Advanced analytics models analyzing claim patterns...</div>
        </div>
      </div>
    </div>
  );
};
